import { A, Route, Router, Routes, hashIntegration, useParams } from "@solidjs/router";
import { Component, ErrorBoundary, JSX, createSignal, onMount } from "solid-js";
import { EditArtworkData, ArtworkImageData, Ad, LivingArtistArtwork, CompressedSelectList } from "./types";
import { Step1 } from "./pages/step-1";
import { Step2 } from "./pages/step-2";
import { EditArtwork } from "./pages/edit-artwork";
import { Introduction } from "./pages/introduction";
import { fetchGet } from "./library/common";
import { EditImage } from "./pages/edit-image";
import { JetMenu } from "./components/jetmenu/jetmenu";
import { LogoSlider } from "./sections/logo-slider";
import { Footer } from "./sections/footer";
import { createStore } from "solid-js/store";
import { Step3 } from "./pages/step-3";
import toast, { Toaster } from "solid-toast";
import { CMSBlock, CMSProvider } from "./components/cms/provider";
import { plainText } from "./components/cms/cms";
import _ from "underscore";
import { getOrSetAsync } from "./API";
import { CreditCardPage } from "./pages/credit-card";
import { PaymentProcessedPage } from "./pages/payment-processed";
import { PaypalConfirmedPage } from "./pages/paypal-confirmed";

export const min = 15;
export const max = 30;
export const [headerContent, setHeaderContent] = createSignal<JSX.Element>(<></>);
export const [ads, setAds] = createSignal<Ad[]>([]);
export const [cms, setCms] = createStore<Record<string,CMSBlock>>({});
export const [cache, setCache] = createStore<Record<string,any>>({});
export const getOrSetCache = async (key: string, getter: () => Promise<any>) => {
    const item = cache[key];
    if (item != undefined) return item;
    const value = await getter();
    setCache(key, value);
    return value;
};

export const loadCms = async (ids: string[]) => {
    const cachedIds = Object.keys(cms);
    const uncachedIds = ids.filter(id => !cachedIds.some(key => key == id));
    if (!uncachedIds.length) return;
    let json: any;
    try {
        const res = await fetchGet("/Home/GetContentBlocksJson", { ids: uncachedIds.join(",") });
        json = await res.json();
        if (!json.Success) return;
    } catch (ex) {
        console.log("loadCms", { ex });
        return;
    }
    let data: Record<string, CMSBlock> = {};
    uncachedIds.forEach(id => {
        let value = json.Data[id];
        if (!value || value.length === 0) {
            data[id] = { found: false, item: null };
            return;
        }
        data[id] = { found: true, item: value };
    });
    setCms(data);
};
export const formatText = (text: string, data: Record<string,any>) => {
    console.log("formatText", { text, data });
    for (let key in data) text = text?.replace(new RegExp('{' + key + '}', 'gi'), data[key]);
    return text;
}
export const getText = (id: string, backup?: string, data?: Record<string, any>) => {
    const html = cms[id]?.item;
    if (!html) {
        return data && backup ? formatText(backup, data) : backup || "";
    }
    let text = plainText(html);
    if (data) {
        text = formatText(text, data);
    }
    return text;
}
export const loadCreditCardCms = async () => {
    const ids = [
        "label::postal-code-zip",
        "label::credit-card-number",
        "label::expiry-mm-yy",
        "label::ccv",
        "label::street",
        "label::pay-with-credit-card",
        "button::cancel",
        "button::pay-now",
        "salon-application/credit-card::total-owing"
    ];
    await loadCms(ids);
}
export const loadIntroductionCms = async () => {
    await loadCms([
        
    ]);
}
export const loadStep1Cms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const loadStep3Cms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const loadStep2Cms = async () => {
    const ids = [
        ""
    ];
    await loadCms(ids);
}
export const getPlainText = (id: string, backup?: string) => {
    const html = cms[id]?.item;
    if (!html) return backup || "";
    return plainText(html);
}
enum Claim {
    IsLoggedIn,
    CanApply,
    IsRegistered,
    HasMinimumArtworks,
    HasMinimumArtworkImages
}
export const getApplicationStatusJson = async (options?: { forceRefresh: boolean }) => getOrSetAsync("application-status", async () => {
    const res = await fetch("/LivingArtistApplication/GetApplicationStatusJson");
    const json = await res.json();
    return json;
}, options);
export const getArtworksCountJson = async (options?: { forceRefresh: boolean }) => getOrSetAsync("artworks-count", async () => {
    const res = await fetch("/LivingArtistApplication/GetArtworkCountJson");
    const json = await res.json();
    return json;
}, options);
export const App : Component = () => {
    const authorised = async (claims: Claim[]) => {
        const json = await getApplicationStatusJson();
        if (!json.Success) {
            toast.error("Unable to contact the server just now. Please try reloading the page.");
            return false;
        }
        const data = json.Data;
        if (claims.some(c => c == Claim.IsLoggedIn)) {
            if (!data.isLoggedIn) {
                toast.error("You must be logged in to access this page.");
                return false;
            }
        }
        if (claims.some(c => c == Claim.CanApply)) {
            if (!data.canApply) {
                if (data.isAdmin) {
                    toast.error("The ARC Scholarship Application has not yet opened: but Admin is granted access.");
                    return true;
                }
                toast.error("The ARC Scholarship Application has not yet opened.");
                return false;
            }
        }
        if (claims.some(c => c == Claim.IsRegistered)) {
            if (!data.isRegistered) {
                toast.error("You must complete the profile form on Step 1 to continue.");
                return false;
            }
        }
        if (claims.some(c => c == Claim.HasMinimumArtworks)) {
            const count = (await getArtworksCountJson()).Data;
            if (count < min) {
                toast.error(`You must submit at least ${min} works.`);
                return false;
            }
        }
        if (claims.some(c => c == Claim.HasMinimumArtworkImages)) {
            const artworks = await getStep2_Data() as LivingArtistArtwork[];
            const hasMissingImages = artworks.some(x => x.Images?.length == 0);
            if (hasMissingImages) {
                toast.error(`Each artwork requires at least one image. Please re-check your artworks.`);
                return false;
            }
        }
        return true;
    }
    const getStep1_Data = async () => {
        const res = await fetch("/LivingArtistApplication/GetStep1DataJson");
        const json = await res.json();
        return json.Data;
    }
    const getStep1_Resources = async () => getOrSetCache("/LivingArtistApplication/GetStep1ResourcesJson", async () => {
        const res = await fetch("/LivingArtistApplication/GetStep1ResourcesJson");
        const json = await res.json();
        return json.Data;
    });
    const uncompress = (list: CompressedSelectList) => list.map(item => ({ Value: item[0], Text: item[1] }));
    const loadStep1 = async ({ navigate }: any) => {
        if (!await authorised([Claim.IsLoggedIn])) {
            return navigate("/not-logged-in");
        }
        const step1 = await getStep1_Data();
        const resources = await getStep1_Resources();
        return {
            step1,            
            resources: {
                Countries: uncompress(resources.Countries),
                Genders: uncompress(resources.Genders)
            }
        } as any;
    };
    const loadStep3 = async ({ navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/step-1");
            }
            if (!await authorised([Claim.HasMinimumArtworks, Claim.HasMinimumArtworkImages])) {
                return navigate("/step-2");
            }
            const creditCard = await getCreditCardInfo();
            return {
                model: {
                    amountOwing: creditCard.artist.amountOwing,
                    applicationStatus: creditCard.artist.applicationStatus
                }
            }
        } catch (ex) {
            log("loadStep3", { ex });
            navigate("/error", { state: { ex } });
        }
    };
    const getStep2_Data = async () => {
        const res = await fetch("/LivingArtistApplication/GetStep2Json");
        const json = await res.json();
        return json.Data;
    };
    const loadStep2 = async ({ navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/step-1");
            }
            const artworks = await getStep2_Data();
            const session = await getApplicationStatusJson();
            return {
                session: session?.Data,
                artworks
            };
        } catch (ex) {
            log("loadStep2", { ex });
            navigate("/error", { state: { ex }});
        }
    }
    const getArtwork = async (id: number) => {
        const response = await fetchGet("/LivingArtistApplication/GetArtworkJson", { id });
        const json = await response.json();
        return json.Data;
    }
    const getArtworkImage = async (artworkId: number, newImageId: number) => {
        const response = await fetchGet("/LivingArtistApplication/GetArtworkImageJson", { artworkId, newImageId });
        const json = await response.json();
        return json.Data as ArtworkImageData;
    }
    const getCreditCardInfo = async () => {
        const response = await fetch("/LivingArtistApplication/GetCreditCardJson");
        const json = await response.json();
        return json.Data;
    }
    const loadCreditCard = async ({ navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/not-authorised");
            }
            if (!await authorised([Claim.HasMinimumArtworks, Claim.HasMinimumArtworkImages])) {
                return navigate("/step-2");
            }
            const info = await getCreditCardInfo();
            return {
                artist: info.artist,
                address: info.address,
                resources: {
                    countries: info.resources.countries
                }
            };
        } catch (ex) {
            log("loadCreditCard", { ex });
            navigate("/error");
        }
    };
    const loadArtworkImage = async ({ params, navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/step-1");
            }
            if (!params.artworkId) {
                return navigate("/not-found");
            }
            if (!params.newImageId) {
                return {
                    IsPrimary: false,
                    IsDeleted: false,
                    Artwork: {
                        Id: params.artworkId
                    }
                };
            }
            const artworkImage = await getArtworkImage(params.artworkId, params.newImageId);
            return artworkImage;
        }
        catch (ex) {
            log("fetchArtworkImageData", { ex });
            navigate("/error", { state: { ex }});
        }
    };
    const  loadIntroduction = async ({ navigate }: any) => {
        try {
            const session = await getApplicationStatusJson();
            log("fetchIntroduction",  { session});
            return { session: session.Data };
        } catch (ex) {
            log("fetchIntroduction", { ex });
            navigate("/error", { state: { ex }});
        }
    }
    const log = (message: string, data: Record<string,any>) => {
        console.log(message, data);
    }
    const getEditArtworkResources = async () => {
        const res = await fetch("/ScholarshipApplication/Application/GetResourcesJson");
        const json = await res.json();
        return json.Data;
    }
    const loadArtwork = async ({ params, navigate }: any) => {
        try {
            if (!await authorised([Claim.IsLoggedIn])) {
                return navigate("/not-logged-in");
            }
            if (!await authorised([Claim.IsRegistered])) {
                return navigate("/step-1");
            }
            const session = (await getApplicationStatusJson()).Data;
            if (params.id == undefined) {
                const data = {
                    session,
                    artwork: {
                        LivingArtistId: session.livingArtistId,
                        Title: "",
                        Medium: "",
                        Width: "",
                        Height: "",
                        Depth: "",
                        UnitOfMeasurement: "ins",
                        Year: "2024",
                        Comment: ""
                    } as any
                } as EditArtworkData;
                console.log("/add-artwork:fetchArtworkData", { data });
                return data;
            }
            const id = params.id as number;
            const artwork = (await getArtwork(id)) as LivingArtistArtwork;
            return {
                session,
                artwork
            } as EditArtworkData;
        } catch (ex) {
            log("fetchArtworkData", { ex });
            navigate("/error");
        }
    };
    const preload = async () => {
        /**
         * CMS
         */
        await loadStep1Cms();
        await loadStep2Cms();
        await loadStep3Cms();
        /**
         * Resources
         */
        await getStep1_Resources();
    };
    const loadPaypalCancelledData = async({ navigate }: any) => {
        try {
            // const params = useParams();
            // const token = params.token;
            toast.error("The Paypal transaction was cancelled on the Paypal website.");
            return loadStep3({ navigate });
        }catch (ex) {
            log("loadPaypalCancelledData", { ex });
            navigate("/error", { state: ex });
        }
    }
    const getUrlSearchParams = () => new URLSearchParams(window.location?.hash?.split("?")[1]);
    const loadPaypalConfirmedData = async({ navigate }: any) => {
        try {
            const params = getUrlSearchParams();
            console.log("loadPaypalConfirmedData", { params });
            return {
                token: params.get("token"),
                paymentId: params.get("paymentId"),
                payerId: params.get("PayerID")
            };
        } catch (ex) {  
            log("loadPaypalConfirmedData", { ex });
            navigate("/error", { state: ex });
        }
    }
    onMount(async() => {
        await preload();
    });
    return (
    <Router source={hashIntegration()}>
        <div class="page-container">
            <CMSProvider cms={cms} setCms={setCms}>
                <Toaster />
                <JetMenu></JetMenu>
                <LogoSlider></LogoSlider>
                <section id="header" style="padding-left: 40px; padding-top: 20px; padding-bottom: 20px;">
                    {headerContent()}
                </section>
                <div class="container" style="padding-bottom: 40px;">
                    <ErrorBoundary fallback={err => {
                        return (
                            <>
                                <h2>
                                    Sorry, there has been an error
                                </h2>
                                <div>
                                    {err.toString()}
                                </div>
                                <div>
                                    Return to the <A href="/">start</A> ...
                                </div>
                            </>
                        )
                    }}>
                        <Routes>
                            <Route path={["/step-1","/my-details"]} component={Step1} data={loadStep1}></Route>
                            <Route path={["/step-2", "/my-artworks"]} component={Step2} data={loadStep2}></Route>
                            <Route path={["/add-artwork","/edit-artwork/:id"]} component={EditArtwork} data={loadArtwork}></Route>
                            <Route path={["/add-image-to-artwork/:artworkId","/edit-image/:artworkId/:newImageId"]} component={EditImage} data={loadArtworkImage}></Route>
                            <Route path={["/step-3"]} component={Step3} data={loadStep3}></Route>
                            <Route path={"/credit-card"} component={CreditCardPage} data={loadCreditCard}></Route>
                            <Route path={"/payment/paypal-confirmed"} component={PaypalConfirmedPage} data={loadPaypalConfirmedData}></Route>
                            <Route path={"/payment/paypal-cancelled"} component={Step3} data={loadPaypalCancelledData}></Route>
                            <Route path={"/payment/processed"} component={PaymentProcessedPage}></Route>
                            <Route path={"/not-logged-in"} component={() => {
                                return (
                                    <div class="blank-page">
                                        <h2>
                                            You must be logged in to access this page
                                        </h2>
                                        <p>
                                            Please <a class="tape" href={"/Home/Login?returnUrl=" + encodeURIComponent("/arc-artist-application")}>log in</a> to your ARC account or <a class="tape" href={"/Home/Register?returnUrl=" + encodeURIComponent("/arc-artist-application")}>register</a> one to proceed.
                                        </p>
                                    </div>                                    
                                )
                            }}></Route>
                            <Route path={"/not-found"} component={() => {
                                return (
                                    <div class="blank-page">
                                        <h2>
                                            Sorry, that page was not found
                                        </h2>
                                        <div>
                                            Return to the <A href="/">start</A> ...
                                        </div>
                                    </div>
                                )
                            }}></Route>
                            <Route path={"/not-authorised"} component={() => {
                                return (
                                    <div class="blank-page">
                                        <h2>
                                            You are not authorised to access the page at this time
                                        </h2>
                                        <div>
                                            Return to the <A href="/">start</A> ...
                                        </div>
                                    </div>
                                );
                            }}></Route>
                            <Route path={"/error"} component={() => {
                                const params = useParams();
                                return (
                                    <div class="blank-page">
                                        <h2>
                                            Sorry, there has been an error
                                        </h2>
                                        <div>
                                            {params.ex?.toString()}
                                        </div>
                                        <div>
                                            Return to the <A href="/">start</A> ...
                                        </div>
                                    </div>
                                )
                            }}></Route>
                            <Route path={["/introduction","/"]} component={Introduction} data={loadIntroduction}></Route>
                            <Route path="*" component={() => {
                                return (
                                    <div class="blank-page">
                                        <h2>
                                            Sorry, that page was not found
                                        </h2>
                                        <div>
                                            Return to the <A href="/">start</A> ...
                                        </div>
                                    </div>
                                )
                            }}></Route>
                        </Routes> 
                    </ErrorBoundary>
                </div>
                <Footer></Footer>
            </CMSProvider>
        </div>
    </Router>);
}